import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { ArrowRight, Phone, Truck, Wrench, Zap, ShieldCheck } from "lucide-react";
import { PHONE, PHONE_HREF } from "@/lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1601997319112-63cce1eabd9d?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Home() {
  return (
    <Layout>
      {/* Hero */}
      <section className="relative min-h-[80vh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D0E11] via-[#0D0E11]/85 to-[#0D0E11]/40" />
        <div className="absolute inset-x-0 top-0 h-2 stripe-warning" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 pt-16 sm:pt-24 pb-20">
          <div className="max-w-3xl">
            <div className="eyebrow mb-4">Greensburg, KY • Est. Serving The 42743</div>
            <h1 className="hero-title text-5xl sm:text-6xl lg:text-7xl text-white">
              From Farm to<br />
              <span className="text-red-500">Jobsite,</span> We Keep<br />
              You Working.
            </h1>
            <p className="mt-6 text-slate-300 text-lg max-w-xl leading-relaxed">
              Hydraulic cylinder rebuilds, chrome rod work, seal replacements, and on-site
              repair for farmers, excavators, and small construction crews.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/intake" data-testid="cta-rebuild" className="btn-red px-8 py-5 rounded-sm inline-flex items-center justify-center gap-2 text-lg">
                Request Cylinder Rebuild <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/onsite" data-testid="cta-onsite" className="btn-outline px-8 py-5 rounded-sm inline-flex items-center justify-center gap-2 text-lg">
                Request On-Site Service <Truck className="w-5 h-5" />
              </Link>
            </div>
            <a href={PHONE_HREF} data-testid="hero-call" className="mt-6 inline-flex items-center gap-3 text-white font-display font-bold text-2xl hover:text-red-500 transition">
              <Phone className="w-6 h-6 text-red-500" /> {PHONE}
            </a>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-y border-[#2A2F3A] bg-[#16181D]">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Wrench, label: "Cylinder Rebuilds" },
            { icon: ShieldCheck, label: "Seals & Chrome Rod" },
            { icon: Truck, label: "50 mi On-Site Radius" },
            { icon: Zap, label: "Emergency Available" },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-600/10 border border-red-600/40 flex items-center justify-center rounded-sm">
                <Icon className="w-5 h-5 text-red-500" />
              </div>
              <div className="font-display font-bold uppercase tracking-wider text-slate-200 text-sm">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Services preview */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-20">
        <div className="eyebrow mb-2">What We Do</div>
        <h2 className="font-display font-black uppercase text-4xl sm:text-5xl text-white">Built for the equipment that pays your bills.</h2>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {[
            { t: "Cylinder Rebuild", d: "Drop off or mail in. Full teardown, honing, new seals, and pressure test.", img: "https://images.pexels.com/photos/39345876/pexels-photo-39345876.png" },
            { t: "Chrome Rod Work", d: "Straightening, re-plating, and replacement rods for pitted or bent stock.", img: "https://images.pexels.com/photos/36532643/pexels-photo-36532643.jpeg" },
            { t: "On-Site Repair", d: "$75/hr, 2 hr min, 50 mile radius. Emergency service for downed equipment.", img: "https://images.pexels.com/photos/16243257/pexels-photo-16243257.jpeg" },
          ].map(s => (
            <div key={s.t} className="shop-card overflow-hidden group">
              <div className="h-48 bg-cover bg-center" style={{ backgroundImage: `url(${s.img})` }} />
              <div className="p-6">
                <div className="font-display font-black text-xl uppercase text-white">{s.t}</div>
                <p className="mt-2 text-slate-400 text-sm leading-relaxed">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <Link to="/services" data-testid="all-services-link" className="btn-outline px-6 py-4 rounded-sm inline-flex items-center gap-2">
            All Services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-red-600 relative">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-mono-tech text-[11px] tracking-[0.24em] uppercase text-red-100">Equipment Down?</div>
            <div className="font-display font-black text-4xl uppercase text-white mt-1">We're on the phone right now.</div>
          </div>
          <a href={PHONE_HREF} data-testid="cta-call-band" className="bg-black text-white font-display font-black uppercase tracking-wider text-xl px-8 py-5 inline-flex items-center gap-3 rounded-sm hover:bg-slate-900 transition">
            <Phone className="w-6 h-6" /> {PHONE}
          </a>
        </div>
      </section>
    </Layout>
  );
}
